<!--
  Copyright (C) 2022 - Jeci SARL - https://jeci.fr

  This program is free software: you can redistribute it and/or modify
  it under the terms of the GNU Affero General Public License as
  published by the Free Software Foundation, either version 3 of the
  License, or (at your option) any later version.

  This program is distributed in the hope that it will be useful,
  but WITHOUT ANY WARRANTY; without even the implied warranty of
  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
  GNU Affero General Public License for more details.

  You should have received a copy of the GNU Affero General Public License
  along with this program.  If not, see https://www.gnu.org/licenses/agpl-3.0.html.
-->

<template>
  <div
    ref="pdfComponent"
    class="pdf-component-root"
    :class="{
      'outline-none': isPdfConsultationPage,
    }"
    tabindex="0"
  >
    <PrimeProgressSpinner
      v-if="!pdf"
      class="loadingRendition"
      aria-label="Loading"
    />
    <div v-else>
      <div class="pdf-toolbar" :class="{ 'pdf-toolbar--small': isSmallScreen }">
        <div v-if="isSinglePageDisplayed" class="pdf-toolbar-group">
          <PrimeButton
            v-tooltip="$t('pdf.previousPage')"
            :aria-label="$t('pdf.previousPage')"
            icon="ri-arrow-left-line"
            class="pdf-toolbar-btn"
            :disabled="isLeftButtonDisable"
            @click="previousPage()"
          />
          <span class="pdf-page-indicator">
            <PrimeInputNumber
              v-model="page"
              input-id="integeronly"
              fluid
              :min="1"
              :max="pages"
              :disabled="pages === 1"
              class="pageInputNumber"
            />
            {{ $t("pdf.on") }} {{ pages }}
          </span>
          <PrimeButton
            v-tooltip="$t('pdf.nextPage')"
            :aria-label="$t('pdf.nextPage')"
            icon="ri-arrow-right-line"
            class="pdf-toolbar-btn"
            :disabled="isRightButtonDisable"
            @click="nextPage()"
          />
        </div>
        <PrimeDivider
          v-if="pages > 1"
          layout="vertical"
          class="pdfActionDivider"
        />
        <div v-if="pages > 1" class="pdf-toolbar-section">
          <div class="pdf-toolbar-group">
            <div>
              <PrimeButton
                v-if="isSinglePageDisplayed"
                v-tooltip.top="$t('pdf.showAllPages')"
                :aria-label="$t('pdf.showAllPages')"
                icon="ri-file-copy-2-line"
                class="pdf-toolbar-btn"
                @click="changePdfDisplay()"
              />
              <PrimeButton
                v-else
                v-tooltip.top="$t('pdf.showPageByPage')"
                :aria-label="$t('pdf.showPageByPage')"
                icon="ri-file-list-line"
                class="pdf-toolbar-btn"
                @click="changePdfDisplay()"
              />
            </div>
          </div>
        </div>
        <PrimeDivider layout="vertical" class="pdfActionDivider" />
        <div class="pdf-toolbar-section">
          <div class="pdf-toolbar-group">
            <PrimeButton
              v-tooltip="$t('pdf.rotationLeft')"
              :aria-label="$t('pdf.rotationLeft')"
              icon="ri-arrow-go-back-line"
              class="pdf-toolbar-btn"
              @click="rotation = rotation - 90"
            />
            <PrimeButton
              v-tooltip="$t('pdf.rotationRigth')"
              :aria-label="$t('pdf.rotationRigth')"
              icon="ri-arrow-go-forward-line"
              class="pdf-toolbar-btn"
              @click="rotation = rotation + 90"
            />
          </div>
        </div>
        <PrimeDivider layout="vertical" class="pdfActionDivider" />
        <div class="pdf-toolbar-section">
          <div class="pdf-toolbar-group">
            <PrimeButton
              v-tooltip="$t('pdf.dezoom')"
              :aria-label="$t('pdf.dezoom')"
              icon="ri-zoom-out-line"
              class="pdf-toolbar-btn"
              @click="zoomOut"
            />
            <PrimeButton
              v-tooltip="$t('pdf.zoom')"
              :aria-label="$t('pdf.zoom')"
              icon="ri-zoom-in-line"
              class="pdf-toolbar-btn"
              @click="zoomIn"
            />
          </div>
        </div>
        <PrimeDivider layout="vertical" class="pdfActionDivider" />
        <div class="pdf-toolbar-section">
          <div class="pdf-toolbar-group">
            <PrimeButton
              v-tooltip.left="$t('pdf.adaptToPage')"
              :aria-label="$t('pdf.adaptToPage')"
              icon="ri-expand-vertical-line"
              class="pdf-toolbar-btn"
              @click="fitAuto()"
            />
            <PrimeButton
              v-tooltip.left="$t('pdf.adaptToWidth')"
              :aria-label="$t('pdf.adaptToWidth')"
              icon="ri-expand-horizontal-line"
              class="pdf-toolbar-btn"
              @click="fitWidthAuto()"
            />
          </div>
        </div>
        <template v-if="hasFormFields && !hideForms">
          <PrimeDivider layout="vertical" class="pdfActionDivider" />
          <div class="flex flex-column align-items-center">
            <div class="flex gap-1 m-2">
              <PrimeButton
                v-if="canSaveForm"
                v-tooltip="$t('pdf.saveForm')"
                :aria-label="$t('pdf.saveForm')"
                icon="ri-save-line"
                severity="info"
                class="text-xl p-1"
                :disabled="!isFormDirty || isSaving"
                :loading="isSaving"
                @click="$emit('saveForm')"
              />
              <PrimeButton
                v-tooltip="$t('pdf.cancelForm')"
                :aria-label="$t('pdf.cancelForm')"
                icon="ri-close-line"
                class="text-xl p-1"
                :disabled="!isFormDirty"
                @click="cancelForm()"
              />
              <PrimeButton
                v-tooltip="$t('pdf.formFieldsInfo')"
                :aria-label="$t('pdf.formFieldsInfo')"
                icon="ri-information-line"
                class="text-xl p-1"
                @click="showFormFieldsInfo()"
              />
            </div>
          </div>
        </template>
      </div>
      <div class="pdf-header">
        <div v-if="isPdfConsultationPage" class="pdf-title-container">
          <h3 class="consultation-title">
            {{ getFileName }}
          </h3>
          <PrimeTag
            v-if="$route.params.versionId"
            class="pdf-version-tag"
            :value="getVersionNumber"
          />
        </div>
      </div>
      <div v-if="isSinglePageDisplayed" class="pdf-single-page">
        <VuePDF
          class="pdf-canvas"
          :pdf="pdf"
          :page="page"
          :rotation="rotation"
          :scale="scale"
          :text-layer="textLayer"
          :annotation-layer="annotationLayer"
          :hide-forms="hideForms"
          @annotation="onAnnotation"
        />
      </div>
      <div v-else class="pdf-all-pages">
        <div v-for="pageItem in pages" :key="pageItem" class="pdf-page-item">
          <VuePDF
            :pdf="pdf"
            :page="pageItem"
            :rotation="rotation"
            :scale="scale"
            :text-layer="textLayer"
            :annotation-layer="annotationLayer"
            :hide-forms="hideForms"
            @annotation="onAnnotation"
          />
          <span class="pdf-page-number">
            {{
              $t("pdf.currentPagination", { page: pageItem, pages: pages })
            }}</span
          >
        </div>
      </div>
    </div>
    <PrimeDialog
      v-model:visible="showFormFieldsDialog"
      :header="$t('pdf.formFieldsInfoTitle')"
      modal
      class="w-8"
    >
      <PrimeDataTable :value="formFieldsData" striped-rows>
        <PrimeColumn field="name" :header="$t('pdf.formFieldName')" sortable />
        <PrimeColumn field="type" :header="$t('pdf.formFieldType')" sortable />
        <PrimeColumn
          field="value"
          :header="$t('pdf.formFieldValue')"
          sortable
        />
      </PrimeDataTable>
    </PrimeDialog>
  </div>
</template>
<script>
import { usePDF, VuePDF } from "@tato30/vue-pdf";
import "@tato30/vue-pdf/style.css";
import { buildPdfSource } from "@/services/PdfConfigService.js";
import { useSelectionStore } from "@/stores/selection.js";
import Tooltip from "primevue/tooltip";
import Button from "primevue/button";
import ProgressSpinner from "primevue/progressspinner";
import Divider from "primevue/divider";
import Tag from "primevue/tag";
import InputNumber from "primevue/inputnumber";
import Dialog from "primevue/dialog";
import DataTable from "primevue/datatable";
import Column from "primevue/column";

export default {
  name: "PDFComponent",
  components: {
    VuePDF,
    PrimeButton: Button,
    PrimeProgressSpinner: ProgressSpinner,
    PrimeDivider: Divider,
    PrimeTag: Tag,
    PrimeInputNumber: InputNumber,
    PrimeDialog: Dialog,
    PrimeDataTable: DataTable,
    PrimeColumn: Column,
  },
  directives: {
    tooltip: Tooltip,
  },
  emits: ["formDirtyChange", "saveForm"],
  props: {
    url: {
      type: String,
      default: null,
    },
    textLayer: {
      type: Boolean,
      default: false,
    },
    annotationLayer: {
      type: Boolean,
      default: false,
    },
    hideForms: {
      type: Boolean,
      default: false,
    },
    canSaveForm: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      pdf: null,
      pages: null,
      page: 1,
      rotation: 0,
      scale: 1,
      defaultViewport: {
        width: 0,
        height: 0,
      },
      isSinglePageDisplayed: true,
      isFormDirty: false,
      isSaving: false,
      hasFormFields: false,
      showFormFieldsDialog: false,
      formFieldsData: [],
      formFieldValues: {},
    };
  },
  computed: {
    getVersionNumber() {
      return `Version ${this.$route.params.versionId}`;
    },
    getFileName() {
      const selectionStore = useSelectionStore();
      if (selectionStore.files.length > 0) {
        return selectionStore.files[0].name;
      } else {
        return "";
      }
    },
    isLeftButtonDisable() {
      return this.page === 1;
    },
    isRightButtonDisable() {
      return this.page === this.pages;
    },
    isPdfConsultationPage() {
      return this.$route.name.startsWith("consultation-pdf");
    },
    isSmallScreen() {
      return window.innerWidth <= 920;
    },
  },
  watch: {
    pages(newValue) {
      if (this.isPdfConsultationPage) {
        const wantedPage = parseInt(this.$route.query.page) || 1;
        this.page =
          wantedPage > newValue ? newValue : wantedPage < 1 ? 1 : wantedPage;
        this.changePageUrl(this.page);
      }
      if (newValue) {
        this.checkFormFields();
      }
    },
    page(newValue) {
      if (this.isPdfConsultationPage) {
        this.changePageUrl(newValue);
      }
    },
  },
  mounted() {
    this.focusOnPDFComponent();
    this.loadPDF();
    this.setDefaultViewport();
    this.fitAuto();
  },
  unmounted() {
    if (this.pdf) {
      this.pdf.destroy();
      if (this.pdf) {
        delete this.pdf;
      }
    }
  },
  methods: {
    changePdfDisplay() {
      this.loadPDF();
      this.isSinglePageDisplayed = !this.isSinglePageDisplayed;
      if (this.isPdfConsultationPage) {
        const url = new URL(window.location.href);
        if (this.isSinglePageDisplayed) {
          this.changePageUrl(this.page);
        } else {
          url.searchParams.delete("page");
          window.history.replaceState(history.state, "", url.toString());
        }
      }
      if (this.isSinglePageDisplayed) {
        this.focusOnPDFComponent();
      }
    },
    setDefaultViewport() {
      if (this.isSmallScreen) {
        this.defaultViewport.width = 300;
        this.defaultViewport.height = 200;
      } else {
        if (this.$route.name === "consultation-pdf") {
          this.defaultViewport.width = 700;
          this.defaultViewport.height = 500;
        } else {
          this.defaultViewport.width = 900;
          this.defaultViewport.height = 700;
        }
      }
    },

    menuDiff() {
      const SM_SIZE = 640;
      const MD_SIZE = 768;
      const LG_SIZE = 1024;

      const SM_MENU_SIZE = 200;
      const MD_MENU_SIZE = 250;
      const LG_MENU_SIZE = 300;
      const XL_MENU_SIZE = 350;

      let menuDiff;
      if (window.innerWidth <= SM_SIZE) {
        menuDiff = SM_MENU_SIZE;
      } else if (window.innerWidth <= MD_SIZE) {
        menuDiff = MD_MENU_SIZE;
      } else if (window.innerWidth <= LG_SIZE) {
        menuDiff = LG_MENU_SIZE;
      } else {
        menuDiff = XL_MENU_SIZE;
      }
      return menuDiff;
    },

    pageWidthScale() {
      if (!this.defaultViewport.width) {
        return 1;
      }
      let availableSize = window.innerWidth - this.menuDiff();
      return availableSize / this.defaultViewport.width;
    },

    pageHeightScale() {
      if (!this.defaultViewport.height) {
        return 1;
      }
      let availableSize = window.innerHeight - this.menuDiff();
      return (availableSize / this.defaultViewport.height) * 0.8;
    },
    zoomIn() {
      this.scale = this.scale < 2 ? this.scale + 0.25 : this.scale;
    },
    zoomOut() {
      this.scale = this.scale > 0.25 ? this.scale - 0.25 : this.scale;
    },
    fitAuto() {
      const widthScale = this.pageWidthScale();
      const heightScale = this.pageHeightScale();
      this.scale = Math.min(widthScale, heightScale);
    },
    fitWidthAuto() {
      this.scale = this.pageWidthScale();
    },
    previousPage() {
      if (!this.isLeftButtonDisable) {
        this.page = this.page > 1 ? this.page - 1 : this.page;
      }
    },
    nextPage() {
      if (!this.isRightButtonDisable) {
        this.page = this.page < this.pages ? this.page + 1 : this.page;
      }
    },
    loadPDF() {
      if (!this.url) {
        console.error("URL is not defined");
        return;
      }
      this.hasFormFields = false;
      this.formFieldValues = {};

      const document = usePDF(buildPdfSource(this.url));
      this.pdf = document.pdf;
      this.pages = document.pages;
      // this.info = document.info
    },
    changePageUrl(pageValue) {
      if (pageValue !== 0) {
        const pageNumber = parseInt(pageValue, 10);
        if (!isNaN(pageNumber)) {
          const newQuery = new URLSearchParams(window.location.search);
          newQuery.set("page", pageNumber);
          window.history.replaceState(
            history.state,
            "",
            `${window.location.pathname}?${newQuery.toString()}`,
          );
        }
      }
    },
    focusOnPDFComponent() {
      if (this.$refs.pdfComponent) {
        this.$refs.pdfComponent.focus();
      }
    },
    async getFormBytes() {
      if (!this.pdf) {
        return null;
      }
      const pdfDoc = await this.pdf.promise;
      try {
        return await pdfDoc.saveDocument();
      } catch (error) {
        console.error("Error saving PDF document:", error);
        return await pdfDoc.getData();
      }
    },
    resetFormDirty() {
      this.isFormDirty = false;
      this.$emit("formDirtyChange", false);
    },
    setSaving(value) {
      this.isSaving = value;
    },
    onAnnotation(value) {
      if (!value) {
        return;
      }
      switch (value.type) {
        case "link":
          if (value.data.url.startsWith("mailto:")) {
            window.location.href = value.data.url;
          } else {
            window.open(value.data.url, "_blank", "noreferrer");
          }
          break;
        case "form-text":
          this.formFieldValues[value.data.fieldName] = value.data.value;
          if (!this.isFormDirty) {
            this.isFormDirty = true;
            this.$emit("formDirtyChange", true);
          }
          break;
        case "form-checkbox":
          this.formFieldValues[value.data.fieldName] = value.data.checked;
          if (!this.isFormDirty) {
            this.isFormDirty = true;
            this.$emit("formDirtyChange", true);
          }
          break;
        case "form-select":
          this.formFieldValues[value.data.fieldName] = value.data.value;
          if (!this.isFormDirty) {
            this.isFormDirty = true;
            this.$emit("formDirtyChange", true);
          }
          break;
        case "form-radio":
          this.formFieldValues[value.data.fieldName] = value.data.value;
          if (!this.isFormDirty) {
            this.isFormDirty = true;
            this.$emit("formDirtyChange", true);
          }
          break;
        case "form-button":
        default:
          console.log("Annotation Event ", value);
      }
    },
    async checkFormFields() {
      if (!this.pdf) {
        this.hasFormFields = false;
        return;
      }
      try {
        const pdfDoc = await this.pdf.promise;
        const fields = await pdfDoc.getFieldObjects();
        this.hasFormFields = fields !== null && Object.keys(fields).length > 0;
      } catch (error) {
        console.error("Error checking form fields:", error);
        this.hasFormFields = false;
      }
    },
    cancelForm() {
      this.loadPDF();
      this.resetFormDirty();
    },
    async showFormFieldsInfo() {
      if (!this.pdf) {
        return;
      }
      try {
        const pdfDoc = await this.pdf.promise;
        const fieldObjects = await pdfDoc.getFieldObjects();
        if (!fieldObjects) {
          this.formFieldsData = [];
        } else {
          const data = [];
          for (const [name, fields] of Object.entries(fieldObjects)) {
            for (const field of fields) {
              const trackedValue = this.formFieldValues[name];
              data.push({
                name: name,
                type: field.type,
                value:
                  trackedValue !== undefined
                    ? trackedValue
                    : (field.value ?? ""),
              });
            }
          }
          this.formFieldsData = data;
        }
        this.showFormFieldsDialog = true;
      } catch (error) {
        console.error("Error getting form fields info:", error);
      }
    },
  },
};
</script>
<style lang="css" scoped>
.pdf-component-root {
  flex-grow: 1;
  overflow: visible;
}

.loadingRendition {
  display: flex;
  margin: 0.5rem auto 0;
}

.pdf-toolbar {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 0.25rem;
}

.pdf-toolbar--small {
  padding-right: 1.5rem;
}

.pdf-toolbar-group {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  margin: 0.5rem;
}

.pdf-toolbar-section {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.pdf-toolbar-btn {
  font-size: 1.25rem;
  padding: 0.25rem;
}

.pdf-page-indicator {
  display: flex;
  align-items: center;
  margin: 0 0.5rem;
  gap: 0.25rem;
}

.pdfActionDivider {
  margin: 0 0.5rem !important;
}

.pdf-header {
  display: flex;
}

.pdf-title-container {
  display: flex;
  margin: 0 auto;
  gap: 0.75rem;
  margin-bottom: 0.75rem;
}

.pdf-version-tag {
  margin: 0 auto;
  font-size: 1.25rem;
}

.pdf-single-page {
  display: flex;
  justify-content: center;
}

.pdf-canvas {
  overflow-y: hidden;
}

.pdf-all-pages {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 1rem;
}

.pdf-page-item {
  display: flex;
  flex-direction: column;
}

.pdf-page-number {
  display: flex;
  justify-content: center;
  padding: 1rem;
}

.pageInputNumber {
  width: 4rem;
}
</style>
